import React, { useState } from "react";

interface McaEntry {
  id: string;
  kind: "assessment" | "meeting" | "dols";
  residentName: string;
  date: string;
  decision: string;
  outcome: string;
  assessor: string;
  evidence: string;
  attendees: string;
  decisionMade: string;
  rationale: string;
  applicationDate: string;
  authorisationDate: string;
  expiryDate: string;
  conditions: string;
  status: string;
}

const KEY = "mca_hub_records";

function load(): McaEntry[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadResidents(): any[] {
  try {
    return JSON.parse(localStorage.getItem("residents_records") || "[]");
  } catch {
    return [];
  }
}

const DECISIONS = ["Residence", "Care & support", "Contact with others", "Finances", "Medication", "Other"];

const blank = (kind: McaEntry["kind"], name: string): McaEntry => ({
  id: `mca-${Date.now()}`,
  kind,
  residentName: name,
  date: new Date().toISOString().slice(0, 10),
  decision: "Residence",
  outcome: "Has capacity",
  assessor: "",
  evidence: "",
  attendees: "",
  decisionMade: "",
  rationale: "",
  applicationDate: "",
  authorisationDate: "",
  expiryDate: "",
  conditions: "",
  status: "Application submitted",
});

export default function McaHub({ user }: { user?: any }) {
  const [entries, setEntries] = useState<McaEntry[]>(load);
  const residents = loadResidents();
  const [form, setForm] = useState<McaEntry | null>(null);
  const [filter, setFilter] = useState<"all" | "assessment" | "meeting" | "dols">("all");
  const [message, setMessage] = useState("");

  const save = (next: McaEntry[]) => {
    setEntries(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const set = (key: keyof McaEntry, value: any) => setForm(f => (f ? { ...f, [key]: value } : f));

  const saveForm = () => {
    if (!form || !form.residentName) {
      setMessage("Choose a resident.");
      return;
    }
    const exists = entries.some(e => e.id === form.id);
    save(exists ? entries.map(e => (e.id === form.id ? form : e)) : [form, ...entries]);
    setForm(null);
    setMessage("Record saved to the MCA/DoLS hub.");
  };

  const remove = (id: string) => save(entries.filter(e => e.id !== id));

  const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

  const assessments = entries.filter(e => e.kind === "assessment");
  const meetings = entries.filter(e => e.kind === "meeting");
  const dols = entries.filter(e => e.kind === "dols");
  const renewalsDue = dols.filter(e => e.expiryDate && daysTo(e.expiryDate) <= 60);

  const shown = filter === "all" ? entries : entries.filter(e => e.kind === filter);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">MCA / DoLS Excellence Hub</h2>
        <p className="text-sm text-slate-600">Capacity assessments per decision, best-interests meetings and DoLS authorisations with renewal alerts - rights protected and evidenced.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{assessments.length}</div><div className="text-xs text-slate-500">Capacity assessments</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{meetings.length}</div><div className="text-xs text-slate-500">Best-interests meetings</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{dols.filter(d => d.status === "Authorised").length}</div><div className="text-xs text-slate-500">Active authorisations</div></div>
        <div className={`rounded-xl border p-4 text-center ${renewalsDue.length ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}`}><div className={`text-2xl font-bold ${renewalsDue.length ? "text-red-700" : "text-green-700"}`}>{renewalsDue.length}</div><div className={`text-xs ${renewalsDue.length ? "text-red-700" : "text-green-700"}`}>Renewals due (60 days)</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {form ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">{form.kind === "assessment" ? "Capacity assessment" : form.kind === "meeting" ? "Best-interests meeting" : "DoLS authorisation"} - {form.residentName}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Date</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            {form.kind === "assessment" ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Decision being assessed</label>
                  <select value={form.decision} onChange={e => set("decision", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    {DECISIONS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Outcome</label>
                  <select value={form.outcome} onChange={e => set("outcome", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    <option>Has capacity</option><option>Lacks capacity</option><option>Assessment postponed</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Assessor</label>
                  <input value={form.assessor} onChange={e => set("assessor", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Evidence (how the person was supported to decide)</label>
                  <textarea value={form.evidence} onChange={e => set("evidence", e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </>
            ) : null}
            {form.kind === "meeting" ? (
              <>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Attendees</label>
                  <input value={form.attendees} onChange={e => set("attendees", e.target.value)} placeholder="Family, social worker, GP, IMCA..." className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Decision made</label>
                  <textarea value={form.decisionMade} onChange={e => set("decisionMade", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Rationale (why this is least restrictive / best interests)</label>
                  <textarea value={form.rationale} onChange={e => set("rationale", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </>
            ) : null}
            {form.kind === "dols" ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Application date</label>
                  <input type="date" value={form.applicationDate} onChange={e => set("applicationDate", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Authorisation date</label>
                  <input type="date" value={form.authorisationDate} onChange={e => set("authorisationDate", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Expiry date</label>
                  <input type="date" value={form.expiryDate} onChange={e => set("expiryDate", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
                  <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    <option>Application submitted</option><option>Authorised</option><option>Expired</option><option>Withdrawn</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Authorisation conditions and how they are met</label>
                  <textarea value={form.conditions} onChange={e => set("conditions", e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </>
            ) : null}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={saveForm} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save record</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Add a record</h3>
          <div className="flex flex-wrap gap-2">
            <select defaultValue="" onChange={e => { if (e.target.value) setForm(blank("assessment", e.target.value)); }} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="">New capacity assessment…</option>
              {residents.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
            <select defaultValue="" onChange={e => { if (e.target.value) setForm(blank("meeting", e.target.value)); }} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="">New best-interests meeting…</option>
              {residents.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
            <select defaultValue="" onChange={e => { if (e.target.value) setForm(blank("dols", e.target.value)); }} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="">New DoLS authorisation…</option>
              {residents.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap gap-2">
          {(["all", "assessment", "meeting", "dols"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} className={filter === f ? "rounded-lg bg-blue-900 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>
              {f === "all" ? "All" : f === "assessment" ? "Assessments" : f === "meeting" ? "Meetings" : "DoLS"}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {shown.map(e => (
            <div key={e.id} className="rounded-lg border border-slate-100 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-900">
                  {e.residentName} <span className="text-xs text-slate-500">{e.kind === "assessment" ? `Capacity - ${e.decision}: ${e.outcome}` : e.kind === "meeting" ? "Best-interests meeting" : `DoLS - ${e.status}`}</span>
                </div>
                <div className="flex items-center gap-2">
                  {e.kind === "dols" && e.expiryDate ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${daysTo(e.expiryDate) < 0 ? "bg-red-100 text-red-700" : daysTo(e.expiryDate) <= 60 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{daysTo(e.expiryDate) < 0 ? "Expired" : `Expires in ${daysTo(e.expiryDate)}d`}</span> : null}
                  <button onClick={() => setForm(e)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Edit</button>
                  <button onClick={() => remove(e.id)} className="text-xs text-red-600">Remove</button>
                </div>
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {e.kind === "assessment" ? `${e.assessor || "Assessor not recorded"} | ${e.date}${e.evidence ? ` | ${e.evidence}` : ""}` : e.kind === "meeting" ? `${e.date} | ${e.attendees || "Attendees not recorded"} | ${e.decisionMade || ""}` : `Applied ${e.applicationDate || "-"} | Authorised ${e.authorisationDate || "-"} | Conditions: ${e.conditions || "-"}`}
              </div>
            </div>
          ))}
          {shown.length === 0 ? <div className="text-sm text-slate-500">No records in this view yet.</div> : null}
        </div>
      </div>
    </div>
  );
}